import { useRef, useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DemoImportStatus } from "@adva/types";
import { api } from "@/lib/api-client";
import { useSwitchAccount, type MyAccountsResponse } from "./use-accounts";

export type DemoSize = "small" | "medium" | "large";
export type DemoMode = "fast" | "full";

// The exact shape POST /core/accounts/demo returns (DemoAccountResponseSchema):
// the account is created synchronously, so `account_id` is available BEFORE the
// first status poll. `batch_id` can be null when the import workflow is disabled
// (the feature-flag path), in which case there is nothing to poll.
interface CreateDemoResponse {
  account_id: string;
  batch_id: string | null;
  status: "provisioning";
  existing: boolean;
}

/**
 * Derived lifecycle of the provisioning + auto-switch flow. A single enum so
 * the dialog renders exactly one honest panel instead of inferring state from
 * a raw status string that says nothing about poll-transport or switch failure
 * (ADV-1277 verify: every mutation/query was happy-path only).
 */
type DemoProvisionPhase =
  | "idle" // no batch in flight
  | "provisioning" // polling, backend still working
  | "switching" // backend done, auto-switch in flight
  | "completed" // backend done AND switch confirmed
  | "switchFailed" // backend done but switch errored — created, not switched
  | "failed" // backend reported failure
  | "timedOut" // poll deadline exceeded with no terminal status
  | "error"; // poll transport failure (401/404/5xx)

interface DemoProvisionResult {
  phase: DemoProvisionPhase;
  /** Jobs the import pipeline has finished so far (drives the progress bar). */
  completedJobs: number;
  /** Total jobs in the provisioning batch (0 until the first poll resolves). */
  totalJobs: number;
  /** Jobs the pipeline reported as failed (surfaced in the failed panel). */
  failedJobs: number;
  accountId: string | undefined;
  /** Poll transport error (statusQuery.error), if any. */
  pollError: unknown;
  /** Auto-switch error, if the post-provision switch failed. */
  switchError: unknown;
  /** Re-attempt the auto-switch after a switchFailed phase. */
  retrySwitch: () => void;
}

// Poll deadlines scale with data volume — a large import legitimately takes
// several minutes, so a one-size deadline would either nag small imports or
// hide a hung large one. Past the deadline we stop polling and surface an
// explicit "taking longer than expected" state instead of spinning forever.
const POLL_DEADLINE_MS: Record<DemoSize, number> = {
  small: 90_000, // ~1.5 min
  medium: 240_000, // ~4 min
  large: 600_000, // ~10 min
};
const POLL_INTERVAL_MS = 1500;
// Bound react-query's own retry so a hard transport error (bad batch_id, expired
// token) surfaces as an error phase instead of retrying invisibly forever.
const STATUS_QUERY_RETRY = 3;

export function useCreateDemoAccount() {
  return useMutation({
    mutationFn: (p: { size?: DemoSize; mode?: DemoMode }) =>
      api.post<CreateDemoResponse>("/core/accounts/demo", p),
    onError: (err) => {
      // Surfaced inline in the dialog (createMutation.isError); log so a
      // failed POST is never swallowed.
      console.error("[demo] create demo account failed", err);
    },
  });
}

export function useDemoProvisionStatus(
  batchId: string | null,
  // Sourced from the CREATE response (DemoAccountResponseSchema.account_id), NOT
  // the status poll — the flat DemoImportStatus the BE returns carries no
  // account_id, so this is the only honest source for the auto-switch target.
  accountId: string | undefined,
  size: DemoSize = "small",
): DemoProvisionResult {
  const switchAccount = useSwitchAccount();
  // Latched to the batchId we've already fired a switch for — dedupes the
  // effect across re-renders. Reset to null on switch failure so a retry can
  // re-fire (ADV-1277: don't permanently latch before the switch resolves).
  const switchedRef = useRef<string | null>(null);
  const startRef = useRef<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [switchError, setSwitchError] = useState<unknown>(null);

  const deadlineMs = POLL_DEADLINE_MS[size];

  const query = useQuery({
    queryKey: ["demo-provision-status", batchId, accountId],
    // The BE returns the flat DemoImportStatus rollup (batch_id, status,
    // total_jobs, completed_jobs, …) — type the response to the real contract
    // instead of a hand-rolled shape that never matched the wire.
    //
    // ADV-1286: forward the CREATED demo account_id as `account_id` so the
    // status poll scopes to the account that OWNS the batch. For an existing
    // user creating a demo, the session/tenant ctx still points at the OLD
    // account until auto-switch fires — without this the BE scopes to the old
    // account, never finds the new account's jobs, never reaches `completed`,
    // and the auto-switch never happens. The BE verifies membership in the
    // forwarded account server-side, so this is not a trust-the-client scope.
    queryFn: () => {
      const qs = accountId
        ? `?account_id=${encodeURIComponent(accountId)}`
        : "";
      return api.get<DemoImportStatus>(
        `/core/accounts/demo/status/${batchId}${qs}`,
      );
    },
    enabled: !!batchId && !timedOut,
    retry: STATUS_QUERY_RETRY,
    // Param type is inferred from the query generics — no hand-rolled shape.
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "completed" || status === "failed"
        ? false
        : POLL_INTERVAL_MS;
    },
  });

  // Normalize the BE's wire status onto our derived lifecycle: "pending" and
  // "importing" both mean "backend still working" → provisioning. "completed"
  // and "failed" are terminal and pass through unchanged.
  const wireStatus = query.data?.status;
  const status: "provisioning" | "completed" | "failed" | undefined =
    wireStatus === "completed" || wireStatus === "failed"
      ? wireStatus
      : wireStatus === "pending" || wireStatus === "importing"
        ? "provisioning"
        : undefined;
  const completedJobs = query.data?.completed_jobs ?? 0;
  const totalJobs = query.data?.total_jobs ?? 0;
  const failedJobs = query.data?.failed_jobs ?? 0;
  const isTerminal = status === "completed" || status === "failed";

  // Reset per-batch latches whenever the batch changes (new attempt / retry).
  useEffect(() => {
    setTimedOut(false);
    setSwitchError(null);
    switchedRef.current = null;
    startRef.current = batchId ? Date.now() : null;
  }, [batchId]);

  // Size-scaled provisioning deadline. Recomputes remaining time from the
  // batch start so re-renders don't restart the clock.
  useEffect(() => {
    if (!batchId || isTerminal || timedOut) return;
    const elapsed = startRef.current ? Date.now() - startRef.current : 0;
    const remaining = Math.max(0, deadlineMs - elapsed);
    const timer = setTimeout(() => {
      setTimedOut(true);
      console.error(
        `[demo] provisioning timed out after ${deadlineMs}ms (batch=${batchId})`,
      );
    }, remaining);
    return () => clearTimeout(timer);
  }, [batchId, isTerminal, timedOut, deadlineMs]);

  const fireSwitch = useCallback(
    (id: string) => {
      setSwitchError(null);
      switchedRef.current = batchId;
      switchAccount.mutate(id, {
        onError: (err) => {
          // The switch (or its refreshSession) failed — un-latch so the user
          // can retry, and record the error so the UI shows "created but
          // couldn't switch you in" instead of a false "switched in".
          setSwitchError(err);
          switchedRef.current = null;
          console.error(
            `[demo] auto-switch after provisioning failed (batch=${batchId})`,
            err,
          );
        },
      });
    },
    [batchId, switchAccount],
  );

  // Auto-switch once provisioning completes. Guarded by switchedRef so it
  // fires at most once per batch (until a failure un-latches it).
  useEffect(() => {
    if (
      status === "completed" &&
      accountId &&
      switchedRef.current !== batchId
    ) {
      fireSwitch(accountId);
    }
  }, [status, accountId, batchId, fireSwitch]);

  const retrySwitch = useCallback(() => {
    if (accountId) fireSwitch(accountId);
  }, [accountId, fireSwitch]);

  const phase: DemoProvisionPhase = (() => {
    if (!batchId) return "idle";
    if (timedOut) return "timedOut";
    if (query.isError) return "error";
    if (status === "failed") return "failed";
    if (status === "completed") {
      if (switchError) return "switchFailed";
      // Only claim "switched in" once the switch mutation actually succeeded.
      if (switchAccount.isSuccess) return "completed";
      return "switching";
    }
    return "provisioning";
  })();

  return {
    phase,
    completedJobs,
    totalJobs,
    failedJobs,
    accountId,
    pollError: query.error,
    switchError,
    retrySwitch,
  };
}

export function useDeleteDemoAccount() {
  const queryClient = useQueryClient();
  const switchAccount = useSwitchAccount();

  return useMutation({
    mutationFn: (demoAccountId: string) =>
      api.delete<{ success: boolean }>(`/core/accounts/demo/${demoAccountId}`),
    onSuccess: async (_data, deletedAccountId) => {
      // Read accounts FRESH from the cache — a snapshot captured at hook-render
      // time can be stale by the time the delete resolves, and racing the
      // manual invalidate against switchAccount's global invalidate could leave
      // the user stranded on a just-deleted account (ADV-1277 verify).
      const fresh = queryClient.getQueryData<MyAccountsResponse>([
        "my-accounts",
      ]);
      // Only switch workspaces when the account we just deleted was the ACTIVE
      // one — deleting an inactive demo (e.g. from Settings → Explore) must not
      // yank the user out of the workspace they're currently in (ADV-1277 Codex
      // P2). When it wasn't active, just refresh the list so the demo disappears.
      const deletedWasActive = fresh?.active_account_id === deletedAccountId;
      if (!deletedWasActive) {
        await queryClient.invalidateQueries({ queryKey: ["my-accounts"] });
        toast.success("Demo account deleted.");
        return;
      }
      const firstNonDemo = fresh?.accounts.find((a) => !a.is_demo);
      if (firstNonDemo) {
        // switchAccount.onSuccess already invalidates every query (including
        // my-accounts), so awaiting it is the single terminal step — the
        // separate manual invalidate is redundant and is dropped.
        await switchAccount.mutateAsync(firstNonDemo.id);
      } else {
        // No non-demo account to land on. At minimum refresh the list so the
        // deleted demo disappears, and warn — the caller is left where they are.
        await queryClient.invalidateQueries({ queryKey: ["my-accounts"] });
        console.warn(
          "[demo] deleted demo account but found no non-demo account to switch to",
        );
      }
      toast.success("Demo account deleted.");
    },
    onError: (err) => {
      // Destructive op — ADR-0035 expects explicit feedback; never swallow.
      console.error("[demo] delete demo account failed", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Could not delete the demo account. Please try again.",
      );
    },
  });
}
